import { UnstyledButton, createStyles } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useApiColumnUpdatePii } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { EntityType } from '@repo/common/enums/entityType';
import type { IconNames } from '@repo/foundations';
import { Text } from '@repo/foundations';
import { useCallback, useMemo } from 'react';
import { Link } from 'react-router';
import type { IBaseModel } from '../../api';
import { queryClient, useAuthUser, useWorkspace } from '../../api';
import { baseQueryHooksFactory, createQueryKeys } from '../../api/factories';
import type { ICommandListItem } from '../../components/Spotlight/components/CommandPalette/constants';
import { PiiIntegrationRender } from '../../components/TableV2/render';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';

export interface IPII extends IBaseModel {
	id: string;
	title: string;
	parent_id: string;
	table: string;
	schema: string;
	database: string;
	integration_name: string;
	integration_id: string;
}

const PII_NAMESPACE = ['table', 'pii'];
const piiQueryKeyFactory = createQueryKeys(PII_NAMESPACE);

export const { usePiiList } = baseQueryHooksFactory<IPII, 'pii'>(
	'pii',
	piiQueryKeyFactory
);

const useStyles = createStyles((theme) => ({
	userAvatarButton: {
		padding: theme.spacing['3xs'],
		borderRadius: theme.other.borderRadius.sm,
		':hover': {
			backgroundColor: theme.other.getColor('fill/transparent/hover'),
		},
	},
}));

export function useColumns<T extends IPII>(): ExtendedDataTableColumn<T>[] {
	const { classes } = useStyles();
	const columns: ExtendedDataTableColumn<T>[] = [
		{
			accessor: 'table',
			titleLabel: 'Table',
			render: (record) => (
				<UnstyledButton className={classes.userAvatarButton}>
					<Link
						to={`/table/${record.parent_id}`}
						style={{ textDecoration: 'none' }}
					>
						<Text size="sm" truncate>
							{record.table ?? ''}
						</Text>
					</Link>
				</UnstyledButton>
			),
		},
		{
			accessor: 'title',
			titleLabel: 'Column',
			render: (record) => (
				<UnstyledButton className={classes.userAvatarButton}>
					<Link to={`/column/${record.id}`} style={{ textDecoration: 'none' }}>
						<Text size="sm" truncate>
							{record.title ?? ''}
						</Text>
					</Link>
				</UnstyledButton>
			),
		},
		{
			accessor: 'database',
			titleLabel: 'Database',
			render: (record) => (
				<Text size="sm" truncate>
					{record.database ?? ''}
				</Text>
			),
		},
		{
			accessor: 'schema',
			titleLabel: 'Schema',
			render: (record) => (
				<Text size="sm" truncate>
					{record.schema ?? ''}
				</Text>
			),
		},
		{
			accessor: 'integration_name',
			titleLabel: 'Integration',
			render: (record) => (
				<PiiIntegrationRender
					integration_name={record.integration_name ?? ''}
					integration_id={record.integration_id ?? ''}
				/>
			),
		},
		{
			accessor: 'integration_id',
			titleLabel: 'Integration Id',
			hidden: true,
			render: (record) => null,
		},
	];

	return columns;
}

export const useActions = (): ICommandListItem<IPII>[] => {
	const { user } = useAuthUser();
	const { workspace } = useWorkspace();
	const { mutateAsync: updatePii } = useApiColumnUpdatePii();

	const handleApplyPII = useCallback(
		async (selectedColumns: IPII[]) => {
			trackEvent('table/pii/update', {}, user, workspace);
			showNotification({
				title: 'Adding PII badges',
				message: 'This may take a few seconds',
				color: 'blue',
			});

			await Promise.all(
				selectedColumns.map((col) =>
					updatePii({
						queryParams: {
							column_id: col.id,
							column_only: true,
						},
					})
				)
			);
			await queryClient.invalidateQueries({
				queryKey: piiQueryKeyFactory.allLists(),
			});

			showNotification({
				title: 'Success',
				message: 'PII badges added to columns',
				color: 'green',
			});
		},
		[updatePii, user, workspace]
	);

	const actions = useMemo(
		() => [
			{
				id: 'actions::apply_pii',
				title: 'Apply PII tag',
				name: 'Apply PII tag',
				iconName: 'PII' as IconNames,
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: true,
				onClick: handleApplyPII,
			},
		],
		[handleApplyPII]
	);

	return actions;
};
